import React from 'react';
import { Collapse, Grid, List, ListItem, ListItemText, Box } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { camelize } from '@utils/strings';
import useStyles from '@components/ticket/styles';
import { TicketStatus } from '@constants/paymentLink';
import logoGray from '../../assets/images/logo_gray.svg';
import { ITicketProps, ITicketItemProps } from './types';
import { DATE_FORMAT_V1, TIME_FORMAT } from '@constants/date';

import useStore from '@hooks/useStore';

const Ticket: React.FC<ITicketProps> = ({
  details,
  link,
  onToggleList = () => null,
  hideTo = 2,
}) => {
  const {
    userFullName,
    ticketNumber,
    title,
    status,
    total,
    payeeName,
    payeeEmail,
    ticketItems,
    paymentDate,
    createdAt,
  } = details;
  const {
    links: {
      paymentLink,
      coBrandingConfig: { secondaryColor },
    },
  } = useStore();
  const { isSubscription } = paymentLink;
  const { t } = useTranslation();
  const css = useStyles({ secondaryColor });
  const [open, setOpen] = React.useState(true);

  React.useEffect(() => {
    onToggleList(open);
  }, [open]);

  const toggleList = () => {
    setOpen(!open);
  };

  const TicketItem: React.FunctionComponent<ITicketItemProps> = ({ item }) => (
    <List data-testid="ticket-item" component="div" disablePadding>
      <ListItem>
        <Box flex={1}>
          <ListItemText
            primary={
              <strong className="item-quantity">
                {t('checkoutScreen.ticket.items.quantity')} {item.quantity}
              </strong>
            }
            secondary={item.name}
          />
        </Box>
        <span className={`${css.price} item-total`}>$ {item.total}</span>
      </ListItem>
    </List>
  );

  const Total: React.FunctionComponent = () => (
    <Grid container className="total-container">
      <h1 className={`${css.price} ticket-total`}>{total}</h1>
    </Grid>
  );

  const To = () => {
    if (hideTo) return null;
    return (
      <Grid container className="payee-info-row">
        <Grid item xs={2}>
          {t('checkoutScreen.ticket.for')}
        </Grid>
        <Grid item xs={10}>
          <div>
            <strong>{payeeName}</strong>
          </div>
          <div>{payeeEmail}</div>
        </Grid>
      </Grid>
    );
  };

  const createdDate = moment(createdAt).format(DATE_FORMAT_V1);
  const createdTime = moment(createdAt).format(TIME_FORMAT);
  const paidAt = moment(paymentDate);
  const paidDate = paidAt.isValid() && paidAt.format(DATE_FORMAT_V1);
  const hasQRCodeClass = link && 'has-qr-code';
  return (
    <div className={css.root}>
      <div data-testid="ticket" className="ticket">
        <section className={css.header}>
          <div className={`ticket-header-info-wrapper ${hasQRCodeClass}`}>
            <Grid container className="ticket-header-info">
              <Grid item className="ticket-header-info-col">
                <label className="ticket-status">
                  <strong className={camelize(status)}>
                    {t(`checkoutScreen.ticket.status.${status}`, '')}
                  </strong>{' '}
                  {status === TicketStatus.PAID && paidDate}
                </label>
              </Grid>
              {link && <Total />}
              <Grid className="ticket-header-info-col">
                <div className="ticket-title">
                  <label className={`${css.text} muted`}>{!isSubscription ? title : 'Folio'}</label>
                </div>
                <div className="ticket-number">
                  <label className={`${css.text} muted`}>
                    {!isSubscription ? ticketNumber : paymentLink.subscription.planName}
                  </label>
                </div>
              </Grid>
            </Grid>
            {!link && <Total />}
          </div>
        </section>
        <section className={css.body}>
          <Grid container className="payee-info">
            {payeeName && <To />}
            <Grid container className="payee-info-row">
              <Grid item xs={2}>
                {t('checkoutScreen.ticket.from')}
              </Grid>
              <Grid item xs={10}>
                <strong>{userFullName}</strong>
              </Grid>
            </Grid>
          </Grid>
          <div className="ticket-list-collapse">
            <ListItem button onClick={toggleList} data-testid="ticket-list-trigger">
              <ListItemText primary={t('checkoutScreen.ticket.details')} />
              {open ? <ExpandLess className={css.icon} /> : <ExpandMore className={css.icon} />}
            </ListItem>
            <Collapse in={open} timeout="auto" unmountOnExit data-testid="ticket-list">
              {ticketItems.map((item, index) => (
                <TicketItem key={index} item={item} />
              ))}
            </Collapse>
          </div>
        </section>
        <section className={css.footer}>
          <div className="ticket-date">
            {t('checkoutScreen.ticket.createAt', {
              date: createdDate,
              time: createdTime,
            })}
            <img className="oyster-logo" src={logoGray} />
          </div>
        </section>
      </div>
    </div>
  );
};

export default Ticket;
